import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usersAPI } from '../services/api';
import { Button } from '../components/Common/Button';
import { Input } from '../components/Common/Input';
import { PasswordInput } from '../components/Common/PasswordInput';
import { Card } from '../components/Common/Card';
import { DoCancelDialog, GoToDialog } from '../components/Common/Dialog';
import AddressInput from '../components/Common/AddressInput';
import { Advice } from '../components/Common/Card';

import { FormLabel, IconHeader } from '../components/Common/Dialog';

import { User, Location, Pass, Delete } from '../components/Icons/CustomIcons';

const SettingsPage = ({ onClose } = {}) => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  
  const [formData, setFormData] = useState({
    vorname: user?.vorname || '',
    name: user?.name || '',
    email: user?.email || '',
    strasse: user?.strasse || '',
    plz: user?.plz || '',
    wohnort: user?.wohnort || '',
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState(null);
  const addressValidateRef = useRef(null);

  // Passwort ändern
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '' });

  // Konto löschen
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate password fields if the user wants to change the password
    if (passwordData.new_password && !passwordData.current_password) {
      setError('Bitte geben Sie Ihr aktuelles Passwort ein.');
      return;
    }
    if (passwordData.new_password && passwordData.new_password.length < 8) {
      setError('Das neue Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    if (formData.strasse.trim()) {
      const addrErr = await addressValidateRef.current?.();
      if (addrErr) { setError(addrErr); return; }
    }

    setIsSaving(true);
    try {
      const response = await usersAPI.updateSettings(formData);
      setUser(response.data);

      if (passwordData.new_password) {
        await usersAPI.changePassword({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        });
        setPasswordData({ current_password: '', new_password: '' });
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Einstellungen');
      console.error('Fehler beim Speichern:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    setError(null);
    try {
      await usersAPI.deleteAccount({});
      setUser(null);
      setShowDeleteConfirm(false);
      setDeleteSuccess(true);
    } catch (err) {
      console.error('Fehler beim Löschen des Kontos:', err);
      setError(err.response?.data?.error || 'Konto konnte nicht gelöscht werden.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <>
    <div className="drawer-page">
      <h2 className="drawer-title">Ihre Daten bearbeiten</h2>
      <p className="drawer-subtitle">
        Ändern Sie Ihre persönlichen Informationen und speichern Sie die Änderungen.
      </p>
    </div>

    <form id="settings-personal-form" onSubmit={handleSubmit} className="modal-content settings-personal-form">
        {/* Fehlermeldung */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Persönliche Daten */}
    <div className="input-section">            
          <IconHeader Icon={<User size={20} color="#fff" />}>   
            Persönliche Daten
          </IconHeader>    
                
          <div className="modal-content__inner">              
            <div className="grid grid-cols-2 gap-2">
              <div className="ml-field">              
                <FormLabel htmlFor="ap-vorname">Vorname</FormLabel>
                <Input
                  id="ap-vorname"
                  type="text" 
                  value={formData.vorname}
                  onChange={(e) => handleChange('vorname', e.target.value)}
                  className="text__input"
                  />
              </div>
              <div className="ml-field">
                <FormLabel htmlFor="ap-nachname">Name</FormLabel>
                <Input
                  id="ap-nachname"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="text__input"
                  required
                />
              </div>
            </div>

            <div className="ml-field">
              <FormLabel htmlFor="email">E-Mail-Adresse</FormLabel>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="text__input"
                placeholder="max.mustermann@email.com"
              />
            </div>
          </div>
        </div>

        {/* Adresse */}
        <div className="input-section">
          <IconHeader Icon={<Location size={20} color="#fff" />}>   
            Adresse
          </IconHeader>          
          
          <AddressInput
            street={formData.strasse}
            plz={formData.plz}
            city={formData.wohnort}
            onChange={({ street, plz, city }) =>
              setFormData(prev => ({ ...prev, strasse: street, plz, wohnort: city }))
            }
            validateRef={addressValidateRef}
            geolocation
          />
        </div>

        {/* Passwort ändern */}
        <div className="input-section">
          <IconHeader Icon={<Pass size={20} color="#fff" />}>
            Passwort ändern
          </IconHeader>

          <div className="modal-content__inner"> 
            <div className="ml-field">
              <FormLabel htmlFor="current_password">Aktuelles Passwort</FormLabel>
              <PasswordInput
                id="current_password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                autoComplete="current-password"
                className="text__input"
              />
            </div>
            <div className="ml-field">
              <FormLabel htmlFor="new_password">Neues Passwort</FormLabel>
              <PasswordInput
                id="new_password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                autoComplete="new-password"
                className="text__input"
              />
            </div>          
            <p className="body Font-S-I-130-T Colors-Grey-06">Mindestens 8 Zeichen, ein Großbuchstabe, ein Kleinbuchstabe und eine Zahl.</p>
            <Advice text="Bitte geben Sie Ihr Passwort sorgfältig ein. Sie können es jederzeit ändern." />
          </div>
        </div>

        <div className="input-section">
        <Card className="settings-account-delete-form">
          <div className="input-section">
            <IconHeader Icon={<Delete size={20} color="#fff" />} danger>
              Konto löschen
            </IconHeader>
            <p className="text-gray-600 mb-4">
              Das Löschen Ihres Kontos ist unwiderruflich. Alle Ihre Daten werden dauerhaft entfernt.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => { setShowDeleteConfirm(true); }}
          ><Delete size={20} color="#000" />
            Konto löschen
          </Button>
        </Card>
        </div>
        {/* ── Sticky save footer ───────────────────────── */}
        <div className="filter-drawer-actions">
          <Button
            type="submit"
            className="settings-save-btn"
            disabled={isSaving}
          >
            {isSaving ? 'Speichern...' : isSaved ? '✓ Gespeichert' : 'Änderungen speichern'}
          </Button>
        </div>
      </form>

      {/* ── Delete-Confirm-Dialog ─────────────────────── */}
      {showDeleteConfirm && (
        <DoCancelDialog
          icon={Delete}
          title="Profil wirklich löschen?"
          text={isDeletingAccount ? 'Wird gelöscht…' : 'Endgültig löschen'}
          message="Alle Daten werden dauerhaft gelöscht und können nicht wiederhergestellt werden."
          onConfirm={handleDeleteAccount}
          onCancel={() => setShowDeleteConfirm(false)}
          danger
        />
      )}

      {/* ── Delete-Success-Dialog ─────────────────────── */}
      {deleteSuccess && (            
        <GoToDialog
          icon={Delete}
          title="Profil gelöscht"
          text="Zur Startseite"
          message="Sie werden zur Startseite weitergeleitet."
          onConfirm={() => navigate('/')}
        />
      )}
    </>
  );
};

export default SettingsPage;